import React, { useState, useEffect, useCallback } from "react";
import { authFetch, API_BASE } from "../api";
import "./DocsModal.css";

export default function DocsModal({ appName, onClose }) {
  const [status, setStatus] = useState("generating");
  const [docs, setDocs] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setStatus("generating");
    setError(null);
    try {
      const genRes = await authFetch(`${API_BASE}/docs/${appName}/generate`, { method: "POST" });
      if (!genRes.ok) {
        const body = await genRes.json().catch(() => ({}));
        throw new Error(body.error || `Generate failed (${genRes.status})`);
      }
      const dataRes = await authFetch(`${API_BASE}/docs/${appName}`);
      if (!dataRes.ok) throw new Error(`Could not load docs (${dataRes.status})`);
      const data = await dataRes.json();
      setDocs(data);
      setStatus("ready");
    } catch (err) {
      setError(err.message || "Unknown error");
      setStatus("error");
    }
  }, [appName]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="docs-overlay" onClick={onClose}>
      <div className="docs-modal" onClick={(e) => e.stopPropagation()}>
        <div className="docs-modal-header">
          <div className="docs-modal-title">
            <h2>{docs ? docs.appName : appName}</h2>
            {docs?.description && <p>{docs.description}</p>}
            {docs?.generatedAt && (
              <div className="docs-modal-meta">
                Generated {new Date(docs.generatedAt).toLocaleString()}
              </div>
            )}
          </div>
          <button className="docs-modal-close" onClick={onClose}>×</button>
        </div>

        {status === "generating" && (
          <div className="docs-modal-loading">
            <div className="docs-spinner" />
            <p>Generating documentation…</p>
          </div>
        )}

        {status === "error" && (
          <div className="docs-modal-error">
            <p>{error}</p>
            <button className="docs-retry-btn" onClick={load}>Retry</button>
          </div>
        )}

        {status === "ready" && docs && <DocsContent docs={docs} />}
      </div>
    </div>
  );
}

function typeBadge(type) {
  const cls =
    type === "free" ? "docs-badge-free" :
    type === "webhook" ? "docs-badge-webhook" :
    type === "paid" ? "docs-badge-paid" :
    "docs-badge-other";
  return <span className={`docs-badge ${cls}`}>{type}</span>;
}

function DocsContent({ docs }) {
  return (
    <div className="docs-modal-body">
      {docs.dataSources && docs.dataSources.length > 0 && (
        <div className="docs-section">
          <div className="docs-section-title">Data Sources</div>
          <table className="docs-table">
            <thead>
              <tr><th>Name</th><th>Endpoint</th><th>Type</th><th>Description</th></tr>
            </thead>
            <tbody>
              {docs.dataSources.map((s, i) => (
                <tr key={i}>
                  <td>{s.name}</td>
                  <td><span className="docs-mono">{s.url}</span></td>
                  <td>{typeBadge(s.type)}</td>
                  <td>{s.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="docs-section">
        <div className="docs-section-title">Required API Keys</div>
        {docs.requiredApiKeys && docs.requiredApiKeys.length > 0 ? (
          <table className="docs-table">
            <thead>
              <tr><th>Key Name</th><th>Service</th><th>Description</th></tr>
            </thead>
            <tbody>
              {docs.requiredApiKeys.map((k, i) => (
                <tr key={i}>
                  <td><span className="docs-mono">{k.name}</span></td>
                  <td>{k.service}</td>
                  <td>{k.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="docs-empty">No API keys required — this app uses free/public APIs.</p>
        )}
      </div>

      {docs.appVariables && docs.appVariables.length > 0 && (
        <div className="docs-section">
          <div className="docs-section-title">App Variables</div>
          <table className="docs-table">
            <thead>
              <tr><th>Variable</th><th>Default</th><th>Description</th><th>Used In</th></tr>
            </thead>
            <tbody>
              {docs.appVariables.map((v, i) => (
                <tr key={i}>
                  <td><span className="docs-mono">{v.name}</span></td>
                  <td>{v.default || "—"}</td>
                  <td>{v.description}</td>
                  <td>{typeBadge(v.usedIn)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {docs.dataRefreshInterval && (
        <div className="docs-section">
          <div className="docs-section-title">Data Refresh</div>
          <div className="docs-stat-chip">
            <span className="docs-stat-value">{docs.dataRefreshInterval.human}</span>
            <span className="docs-stat-label">({docs.dataRefreshInterval.seconds}s)</span>
          </div>
        </div>
      )}

      {docs.setupInstructions && (
        <div className="docs-section">
          <div className="docs-section-title">Setup Instructions</div>
          <ol className="docs-setup-list">
            {docs.setupInstructions
              .split("\n")
              .filter((s) => s.trim())
              .map((s, i) => <li key={i}>{s.replace(/^\d+\.\s*/, "")}</li>)}
          </ol>
        </div>
      )}

      {docs.filesSummary && Object.keys(docs.filesSummary).length > 0 && (
        <div className="docs-section">
          <div className="docs-section-title">File Overview</div>
          <table className="docs-table">
            <thead>
              <tr><th>File</th><th>Purpose</th></tr>
            </thead>
            <tbody>
              {Object.entries(docs.filesSummary).map(([file, summary]) => (
                <tr key={file}>
                  <td><span className="docs-mono">{file}</span></td>
                  <td>{summary}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
